import { createFileRoute, redirect, useNavigate, useRouter } from '@tanstack/react-router'
import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { Loader2, Lock } from 'lucide-react'

import { getContract, getContractPdf, getContractClauses, markContractReviewed, unmarkContractReviewed, getContractRuns, getContractClausesByRun, appendClauseComments, appendContractComments, appendHighlightComments } from '@/services'
import { evaluateContractRisk } from '@/services/contractService'
import type { ExtractionRunSummary } from '@/services'
import { getSharedWithMe } from '@/services/contractService'
import { notifications } from '@mantine/notifications'
import { ForbiddenError } from '@/services/api'
import {
  getManagementAccount,
} from '@/services/managementAccountService'
import type { Contract, ContractPdf, Clause, ClauseComment, ContractComment, AccessLevel } from '@/types'
import type { PendingPdfHighlight, TempUserClause, DetailValueEdit } from '@/types/contract-analysis'
import { getContractAccessLevel, getProjectAccessLevel } from '@/services/userManagementService'
import { useVaultContext } from './-vault-context'
import { useAuth, useClientProject, useOpenDocuments } from '@/contexts'
import { isUuid, normalizeEntityId, toShortEntityId } from '@/lib/entityUrl'
import { goToContract } from '@/lib/clauseNav'

// Custom hooks
import { usePdfRenderer } from '@/hooks/usePdfRenderer'
import { useClauseAnnotations } from '@/hooks/useClauseAnnotations'
import { useClauseHighlighting } from '@/hooks/useClauseHighlighting'
import { useTaxonomy } from '@/hooks/useTaxonomy'
import { useAnalysisScrollSync } from './-analysis/useAnalysisScrollSync'
import { usePdfTextSelection } from './-analysis/usePdfTextSelection'
import { useClauseCommentsState } from './-analysis/useClauseCommentsState'
import { useContractSaves } from './-analysis/useContractSaves'
import { usePendingStore } from './-analysis/pendingStore/usePendingStore'
import { deriveClauses } from './-analysis/pendingStore/deriveClauses'
import { deriveHighlights } from './-analysis/pendingStore/deriveHighlights'
import type { NewClauseChange } from './-analysis/pendingStore/types'
// DetailValueChange is upserted via pendingStore below; its edit shape (DetailValueEdit) comes from the rail.
import { classifyCommitError } from './-analysis/commitErrorTaxonomy'
import { refreshContractSnapshot } from './-analysis/refreshContractSnapshot'
import { usePdfHighlightOverlays } from './-analysis/usePdfHighlightOverlays'
import { buildRailTools } from './-analysis/buildRailTools'

// Components
import { DocumentWorkbenchSkeleton } from '@/components/route-shell/skeletons/DocumentWorkbenchSkeleton'
import { PdfViewer } from '@/components/contract-analysis/PdfViewer'
import { ContractTopBar } from '@/components/contract-analysis/ContractTopBar'
import { AnalysisRightRail } from '@/components/contract-analysis/AnalysisRightRail'
import { ClauseTagSelectorModal } from '@/components/contract-analysis/ClauseTagSelectorModal'
import { FloatingClauseDetails } from '@/components/contract-analysis/FloatingClauseDetails'
import { SaveBar } from '@/components/contract-analysis/SaveBar'
import { SaveChangesModal, type ChangeGroup } from '@/components/contract-analysis/SaveChangesModal'
import { UnsavedChangesPrompt } from '@/components/contract-analysis/UnsavedChangesPrompt'
import { MentionUsersProvider } from '@/components/comments/MentionUsersContext'
import { MaterialSearchProvider } from '@/components/comments/MaterialSearchContext'
import { CreateTaskModal } from '@/components/Tasks'
import { RiskDetailModal } from '@/components/contract-analysis/RiskDetailModal'
import { riskFindingPrefill } from '@/lib/taskPrefill'
import type { RiskFinding } from '@/types/contract'
import type { CreateTaskPrefill } from '@/types/task'
import { useUnsavedChangesGuard } from './-analysis/useUnsavedChangesGuard'
import { usePendingStorePersistence } from './-analysis/usePendingStorePersistence'

import '@/styles/pages.css'
import '@/styles/analysis.css'

const ANNOTATION_RENDER_DELAY_MS = 100

// The top-most bounding box of a clause (smallest page, then smallest y), or undefined.
// A clause's boundingBoxes arrive from the graph in an unspecified order (HAS_BOUNDING_BOX
// is an unordered relationship), so boundingBoxes[0] is NOT reliably the top box — for a
// clause spanning a page break it can be the lower-page box. Use this for the scroll target
// and list ordering so navigation lands on the clause's first (top) page.
type ClauseBoundingBox = NonNullable<Clause['boundingBoxes']>[number]
function topMostBox(clause: Clause): ClauseBoundingBox | undefined {
  const boxes = clause.boundingBoxes
  if (!boxes || boxes.length === 0) return undefined
  return boxes.reduce((top, b) =>
    b.pageNumber < top.pageNumber || (b.pageNumber === top.pageNumber && b.y < top.y) ? b : top
  )
}

// Helper: sort clauses by PDF position (page, then vertical offset).
// Backend bounding boxes position a clause before any page has rendered; span
// matching (which needs rendered text layers) is only the legacy fallback.
function sortClausesByPdfPosition(
  clauses: Clause[],
  clauseAnnotations: ReturnType<typeof useClauseAnnotations>,
  spansWithPages: ReturnType<typeof usePdfRenderer>['spansWithPages'],
): Clause[] {
  const textWithSpanBoundaries = clauseAnnotations.concatenateWithSpanBoundaries(spansWithPages)

  const positionOf = (clause: Clause): { page: number; y: number } | null => {
    const box = topMostBox(clause)
    if (box) return { page: box.pageNumber, y: box.y }
    const coord = clause.coordinates?.[0]
    if (coord) return { page: coord.topLeft.pageNum, y: coord.topLeft.y }
    const spans = clauseAnnotations.getBoundingSpans(clause.text || '', textWithSpanBoundaries)
    if (!spans?.startSpan) return null
    return { page: spans.startSpan.pageNum, y: spans.startSpan.textSpanEl.offsetTop }
  }

  const positions = new Map<string, { page: number; y: number } | null>()
  clauses.forEach((c) => positions.set(c.uuid, positionOf(c)))

  return [...clauses].sort((a, b) => {
    const pa = positions.get(a.uuid)
    const pb = positions.get(b.uuid)
    if (!pa || !pb) return 0
    if (pa.page !== pb.page) return pa.page - pb.page
    return pa.y - pb.y
  })
}

interface AnalysisSearchParams {
  /** Deep-link clause bookmark (view state riding on the contract URL). Either id form. */
  clause?: string
  /** 'managementAccount' renders a management account through the same viewer (ADR 0004). */
  kind?: 'managementAccount'
}

/** What the loader delivers before the workbench mounts (ADR 0027). */
export interface AnalysisLoaderData {
  contractData: Contract
  pdfResponse: ContractPdf | null
  clausesData: Clause[]
}

export const Route = createFileRoute('/_app/law/vault/analysis/$contractId')({
  // Fallback title until the contract loads; the page then sets document.title to the contract's
  // name (see the effect in ContractAnalysisContent) so history distinguishes contracts.
  head: () => ({ meta: [{ title: 'Contract Analysis · AERRIS' }] }),
  component: ContractAnalysisContent,
  // The URL identifies the contract in its canonical short (base62) form; components always see
  // the decoded UUID (parse) and links always emit the short form (stringify). The URL carries
  // ONLY the document id — its project/client are derived from the loaded contract, never from
  // search params.
  params: {
    parse: (params) => ({ contractId: normalizeEntityId(params.contractId) ?? params.contractId }),
    stringify: (params) => ({ contractId: toShortEntityId(params.contractId) }),
  },
  // Canonicalize legacy dashed-UUID links to the short form. replace: the pre-shortener URL
  // shouldn't linger as a separate history entry. Only this route's own view state survives the
  // hop — the identity spray old links carry (clientId/projectId/contractId, still valid search
  // keys at the _app level) is dropped, since this page derives that context from the contract.
  beforeLoad: ({ params, location }) => {
    if (isUuid(location.pathname.split('/').pop() ?? '')) {
      throw redirect({
        to: '/law/vault/analysis/$contractId',
        params: { contractId: params.contractId },
        search: (prev) => ({ clause: prev.clause, kind: prev.kind }),
        replace: true,
      })
    }
  },
  validateSearch: (search: Record<string, unknown>): AnalysisSearchParams => ({
    clause: typeof search.clause === 'string' ? search.clause : undefined,
    kind: search.kind === 'managementAccount' ? 'managementAccount' : undefined,
  }),
  // Only the kind refetches the document; the clause bookmark is pure view state and must
  // never re-run the loader (it changes on every clause click via goToContract).
  loaderDeps: ({ search }) => ({ kind: search.kind }),
  // The critical path the skeleton covers (ADR 0027): document ∥ PDF, then the clause read —
  // project-scoped, so it can only follow the contract (the URL carries no project). A
  // management account is CSV-only: one read, rows ride along as clause-shaped entries, no PDF.
  // A 403 on any primary read routes to Access Denied, exactly as the old in-component load did.
  loader: async ({ params, deps }): Promise<AnalysisLoaderData> => {
    try {
      if (deps.kind === 'managementAccount') {
        const contractData = await getManagementAccount(params.contractId)
        return { contractData, pdfResponse: null, clausesData: contractData.clauses ?? [] }
      }
      const [contractData, pdfResponse] = await Promise.all([
        getContract(params.contractId),
        getContractPdf(params.contractId),
      ])
      const clausesData = await getContractClauses(contractData.projectUuid ?? '', params.contractId)
      return { contractData, pdfResponse, clausesData }
    } catch (error) {
      if (error instanceof ForbiddenError) throw redirect({ to: '/unauthorized' })
      throw error
    }
  },
  pendingComponent: DocumentWorkbenchSkeleton,
})

function ContractAnalysisContent() {
  const { contractId } = Route.useParams()
  const { clause: clauseFromSearch, kind } = Route.useSearch()
  // Never null: the pendingComponent holds the screen until the loader has delivered.
  const loaded = Route.useLoaderData()
  const router = useRouter()
  const isManagementAccount = kind === 'managementAccount'
  const { allProjects } = useVaultContext()
  const { currentUser } = useAuth()
  const { setDerivedScope } = useClientProject()
  const { openDocument } = useOpenDocuments()
  const navigate = useNavigate()

  // Core state
  const [contract, setContract] = useState<Contract | null>(null)
  // Project for the project-scoped write endpoints: always the loaded document's own project
  // (contracts carry it directly; management accounts get it from the owning-project lookup).
  const projectUuid = contract?.projectUuid ?? ''
  const [pdfData, setPdfData] = useState<ContractPdf | null>(null)
  const [loading, setLoading] = useState(true)
  const [pdfZoom, setPdfZoom] = useState<number | null>(null)
  // The caller's resolved access level for this document. null until resolved (or on failure) —
  // treated as read-only (deny-by-default) so edit controls never flash for a READER mid-load.
  const [accessLevel, setAccessLevel] = useState<AccessLevel | null>(null)
  // Contract-only mode (ADR 0025): the caller reaches this document through a direct contract or
  // folder grant and holds NO access to its project — the project access lookup 403s while the
  // contract cascade grants a level. Cross-document affordances (links into sibling contracts)
  // then redact rather than dead-end on /unauthorized.
  const [contractOnly, setContractOnly] = useState(false)
  // The caller's PROJECT-level access, when they have any. Contract-level Effective Access is not
  // enough for operations that write beyond this one document (risk re-assessment evaluates the
  // whole fold family and is @projectAuth.canEdit-gated server-side) — a contract-only EDITOR must
  // not be shown a button that can only 403.
  const [projectAccessLevel, setProjectAccessLevel] = useState<AccessLevel | null>(null)
  // In contract-only mode: the caller's other granted contracts ("Shared with me"), so a link to a
  // sibling document they DO hold stays a link. null while unresolved → treated as not accessible.
  const [sharedContractUuids, setSharedContractUuids] = useState<Set<string> | null>(null)

  // Extraction-run history (read-only "view a previous version"). `runs` is newest-first;
  // `selectedRunUuid` null = the current (live) run. Management accounts have no runs.
  const [runs, setRuns] = useState<ExtractionRunSummary[]>([])
  const [selectedRunUuid, setSelectedRunUuid] = useState<string | null>(null)
  const currentRun = useMemo(() => runs.find((r) => r.current) ?? null, [runs])
  // Viewing a prior run → the whole surface is read-only (its clauses are superseded nodes;
  // every mutation targets live clauses, so editing here would be meaningless or wrong).
  const viewingHistory = selectedRunUuid != null && selectedRunUuid !== currentRun?.runUuid

  // EDITOR+ on this document. Mirrors the server's @contractAuth.canEdit, which gates every mutation
  // on this page. A null access level (not yet resolved, or the lookup failed) means no edit access —
  // deny-by-default so edit controls never appear for a READER, even momentarily during load.
  const hasEditAccess = accessLevel === 'ADMIN' || accessLevel === 'EDITOR'
  // The whole edit surface is read-only when viewing a prior run OR when the user can't edit.
  const readOnly = viewingHistory || !hasEditAccess

  const notifyReadOnly = useCallback(() => {
    notifications.show({
      color: 'gray',
      message: viewingHistory
        ? 'Viewing a previous version — read-only.'
        : 'You have read-only access to this contract.',
    })
  }, [viewingHistory])

  // Switch the viewer between the current run and a prior one. The current run goes through the
  // normal clause read (it also carries USER clauses + live flags); a prior run uses the run-scoped
  // read, which reaches its superseded clauses directly. Either way we just swap contract.clauses,
  // so the existing PDF-annotation / clause-list rendering shows the selected run unchanged.
  const handleSelectRun = useCallback(
    async (runUuid: string | null) => {
      const target = !runUuid || runUuid === currentRun?.runUuid ? null : runUuid
      setSelectedRunUuid(target)
      setSelectedClause(null)
      try {
        const clauses = target
          ? await getContractClausesByRun(projectUuid, contractId, target)
          : await getContractClauses(projectUuid, contractId)
        setContract((prev) => (prev ? { ...prev, clauses } : prev))
      } catch (e) {
        console.error('Failed to load run clauses:', e)
        notifications.show({ color: 'red', message: 'Failed to load that version.' })
      }
    },
    [projectUuid, contractId, currentRun?.runUuid],
  )

  // Clause review state
  const [selectedClause, setSelectedClause] = useState<Clause | null>(null)
  const [aiAnnotationsVisible, setAiAnnotationsVisible] = useState(true)
  const [userAnnotationsVisible, setUserAnnotationsVisible] = useState(true)
  // Clause whose extracted-details panel floats beside its PDF annotation (null = closed).
  // Held as a uuid, not a Clause: the panel re-reads the clause from the current derived set
  // each render, so saves/pending edits refresh it and a deleted clause closes it.
  const [detailsClauseUuid, setDetailsClauseUuid] = useState<string | null>(null)
  // Bumped after each save so an open Change History panel refetches its entries.
  const [historyRefresh, setHistoryRefresh] = useState(0)
  // The create-Task form's prefill, set by whichever affordance asked for it (a validity issue, a
  // clause, the Tasks tab) — non-null means the modal is open. One modal for the whole page, so it
  // survives switching rail tabs while it is open.
  const [taskPrefill, setTaskPrefill] = useState<CreateTaskPrefill | null>(null)
  // Bumped after a Task is created so an open Tasks panel refetches.
  const [taskRefresh, setTaskRefresh] = useState(0)

  // The risk detail modal's scope: null = closed, '' = the whole contract, a uuid = that clause.
  // One modal for the page (the top-bar badge and every clause dot open the same one), because it
  // needs the contract, the create-Task seam and the refetch — all of which live up here.
  const [riskDetailScope, setRiskDetailScope] = useState<string | null>(null)
  // True while POST .../risk/.../evaluate is in flight, so the footer action cannot be double-fired
  // (an evaluation with a published policy is an LLM call per contract — slow enough to invite it).
  const [reassessing, setReassessing] = useState(false)

  // UI state
  const [currentPage, setCurrentPage] = useState(1)
  // Kept in lockstep with `RailToolId` (AnalysisRightRail.tsx) — a tool missing from this union can
  // never become the active one, so the panel would silently never open.
  const [activeRailTool, setActiveRailTool] = useState<
    | 'summary'
    | 'tagged'
    | 'flagged'
    | 'addTag'
    | 'highlights'
    | 'comment'
    | 'history'
    | 'tasks'
    | 'chat'
    | null
  >('tagged')
  const [tagSelectorClauseId, setTagSelectorClauseId] = useState<string | null>(null)

  // Ref to track selected clause for callbacks (avoids stale closure).
  const selectedClauseRef = useRef<Clause | null>(null)
  selectedClauseRef.current = selectedClause

  // Dedupes the auto-select-from-search effect to one run per deep-link. Holds the
  // `contractId:clause` it last acted on; cleared on every contract change (below)
  // so re-following the same clause link (which always routes via another contract)
  // highlights again instead of being swallowed as a repeat of the same key.
  const autoSelectedKeyRef = useRef<string | null>(null)

  // ---- Extracted state hooks ----
  const commentsState = useClauseCommentsState()
  const pendingStore = usePendingStore()
  // Per-customer taxonomy — supplies each extracted detail's value type + enum options so the
  // details panel can render a type-appropriate editor. Keyed to the current client (set during load).
  const taxonomy = useTaxonomy()

  // Persist/restore unsaved pending edits across reloads, keyed by contractId. Owns the store's
  // per-contract lifecycle (restores on contract switch), replacing the old reset-effect clear.
  usePendingStorePersistence(contractId, pendingStore.changes, pendingStore.setChanges)

  // Single render-time overlay: every clause-consuming surface (the imperative PDF
  // chip layer, the rail lists) reads `server snapshot ⊕ pending store` from here,
  // never `contract.clauses` directly. With an empty store — or while viewing a
  // prior run — this IS the snapshot reference, so rendering is byte-identical to
  // before; once edits move into the store (Phase C) they surface here.
  const derivedClauses = useMemo(
    () => deriveClauses(contract?.clauses, pendingStore.changes, viewingHistory),
    [contract?.clauses, pendingStore.changes, viewingHistory],
  )

  // ── Comments post immediately on "Done" ──
  // Comments are not document changes: they are additive, need no undo, and are excluded from the
  // change history — so they never enter the pending store or the Save flow. Each commit POSTs to
  // the comment append endpoint right away; the draft stays in the composer until the POST
  // succeeds (nothing is lost on failure), then the new comment is appended to the local snapshot
  // (the server MERGEs on clientId, so a later refetch can't duplicate it).

  // Guards against a double-post (rapid "Done" clicks / Cmd+Enter) for the same target.
  const committingCommentRef = useRef<Set<string>>(new Set())

  const handleCommitClauseComment = useCallback(
    async (clauseId: string) => {
      if (committingCommentRef.current.has(clauseId)) return
      const draft = (commentsState.clauseComments[clauseId] || '').trim()
      if (!draft || !currentUser) return
      committingCommentRef.current.add(clauseId)
      const clientId = crypto.randomUUID()
      const createdAt = new Date().toISOString()
      try {
        await appendClauseComments(projectUuid, clauseId, [
          { comment: draft, createdAt, userId: currentUser.uuid, clientId },
        ])
        const posted: ClauseComment = {
          uuid: clientId,
          comment: draft,
          createdAt,
          userId: currentUser.uuid,
          userName: currentUser.name || currentUser.email || undefined,
        }
        setContract((prev) =>
          prev
            ? {
                ...prev,
                clauses: (prev.clauses || []).map((c) =>
                  c.uuid === clauseId ? { ...c, comments: [...(c.comments || []), posted] } : c,
                ),
              }
            : prev,
        )
        commentsState.setClauseComments((prev) => {
          const next = { ...prev }
          delete next[clauseId]
          return next
        })
      } catch (e) {
        notifications.show({ color: 'red', message: classifyCommitError(e).message })
      } finally {
        committingCommentRef.current.delete(clauseId)
      }
    },
    [commentsState, currentUser, projectUuid],
  )

  // Document-level comment commit — same immediate-post gesture; management accounts keep their
  // own append endpoint (their nodes don't carry the Contract label).
  const handleCommitContractComment = useCallback(async () => {
    const key = `doc:${contractId}`
    if (committingCommentRef.current.has(key)) return
    const draft = commentsState.contractComment.trim()
    if (!draft || !currentUser) return
    committingCommentRef.current.add(key)
    const clientId = crypto.randomUUID()
    const createdAt = new Date().toISOString()
    try {
      // Management accounts are read-only (comments dropped for now); only contracts persist comments.
      if (isManagementAccount) return
      await appendContractComments(projectUuid, contractId, [
        { comment: draft, createdAt, userId: currentUser.uuid, clientId },
      ])
      const posted: ContractComment = {
        uuid: clientId,
        comment: draft,
        createdAt,
        userId: currentUser.uuid,
        userName: currentUser.name || currentUser.email || undefined,
      }
      setContract((prev) => (prev ? { ...prev, comments: [...(prev.comments || []), posted] } : prev))
      commentsState.setContractComment('')
    } catch (e) {
      notifications.show({ color: 'red', message: classifyCommitError(e).message })
    } finally {
      committingCommentRef.current.delete(key)
    }
  }, [commentsState, currentUser, projectUuid, contractId, isManagementAccount])

  // Reviewer-created clauses awaiting save live in the pending store as `newClause`
  // containers (clause + its tags + justifications); deriveClauses materializes them into
  // `derivedClauses`. These views expose the raw containers for the tag selector, the
  // unsaved-count, and the save handler.
  const newClauseChanges = useMemo(
    () =>
      Object.values(pendingStore.changes).filter(
        (c): c is NewClauseChange => c.kind === 'newClause',
      ),
    [pendingStore.changes],
  )

  // Committed-but-unsaved extracted-detail value edits, keyed `${justificationUuid}:${detailName}`,
  // so the details editor renders the pending value over the server one until Save.
  const pendingDetailValues = useMemo(() => {
    const out: Record<string, { value: string; values?: string[] }> = {}
    for (const c of Object.values(pendingStore.changes)) {
      if (c.kind === 'detailValue') {
        out[`${c.justificationUuid}:${c.detailName}`] = { value: c.value, values: c.values }
      }
    }
    return out
  }, [pendingStore.changes])

  // Record a detail-value edit as a pending delta (deferred to Save, like flag/comment). Keyed by
  // (justification, detailName), so re-editing the same detail coalesces to the latest value — and
  // an edit that restores the server state (back to the extracted value, or an addition cleared to
  // empty) drops the delta entirely, mirroring flag↔unflag coalescing.
  const handleDetailValueChange = useCallback(
    (edit: DetailValueEdit) => {
      if (edit.matchesServer) {
        pendingStore.remove(`detailValue:${edit.justificationUuid}:${edit.detailName}`)
        return
      }
      pendingStore.upsert({
        kind: 'detailValue',
        justificationUuid: edit.justificationUuid,
        clauseUuid: edit.clauseUuid,
        detailName: edit.detailName,
        value: edit.value,
        values: edit.values,
      })
    },
    [pendingStore],
  )

  // Highlight creates + saved-highlight deletes also live in the store; both are deferred until
  // Save (creation records a delta, deletion records a delta — no eager API call). The overlay
  // renders pending creates and hides saved highlights with a pending delete.
  const onCreateHighlight = useCallback(
    (highlight: PendingPdfHighlight) => {
      pendingStore.upsert({ kind: 'highlightCreate', clientId: highlight.id, highlight })
    },
    [pendingStore],
  )

  const handleSetPendingHighlightComment = useCallback(
    (id: string, comment: string) => {
      const existing = pendingStore.changes[`highlightCreate:${id}`]
      if (existing?.kind === 'highlightCreate') {
        pendingStore.upsert({ ...existing, highlight: { ...existing.highlight, comment } })
      }
    },
    [pendingStore],
  )

  const handleRemovePendingHighlight = useCallback(
    (id: string) => pendingStore.remove(`highlightCreate:${id}`),
    [pendingStore],
  )

  const handleDeleteSavedHighlight = useCallback(
    (uuid: string) => pendingStore.upsert({ kind: 'highlightDelete', highlightUuid: uuid }),
    [pendingStore],
  )

  // Total unsaved work = the store's deltas. Comment composer text is NOT unsaved document work —
  // comments post immediately on Done. Drives the SaveBar, the mark-reviewed gate, and the nav guard.
  const pendingCount = pendingStore.count

  // ---- PDF renderer + clause annotations + clause highlighting ----
  const pdfRenderer = usePdfRenderer({
    onError: (error) => console.error('PDF rendering failed:', error),
  })

  const { syncColumnsToClause } = useAnalysisScrollSync(
    pdfRenderer.pageWrapperRef as React.RefObject<HTMLDivElement>,
    pdfRenderer.ensurePageRendered,
  )

  // 1-based PDF page a clause sits on (bounding box, falling back to coordinates),
  // used to force-render a virtualized page before scrolling the PDF to it.
  const clausePageNumber = useCallback(
    (clause: Clause): number | undefined =>
      topMostBox(clause)?.pageNumber ?? clause.coordinates?.[0]?.topLeft.pageNum,
    [],
  )

  // Inline-tag click from PDF annotations: select clause and scroll columns.
  const handleInlineTagClick = useCallback(
    (clauseId: string) => {
      const clause = derivedClauses.find((c) => c.uuid === clauseId)
      if (!clause) return

      setSelectedClause(clause)
      const isUserCreated = clause.isUserCreated === true
      if (isUserCreated) setUserAnnotationsVisible(true)
      else setAiAnnotationsVisible(true)

      // Anchor the sync on the inline tag's current Y in the PDF so the clause
      // list scrolls to the same vertical position rather than wherever the
      // list item happens to be.
      setTimeout(
        () =>
          requestAnimationFrame(() => {
            const pdfAnnotation = document.querySelector(
              `[data-clause-id="${clauseId}"]`,
            ) as HTMLElement | null
            const zone = document.querySelector('.analysis-two-zone')
            let fallbackY = 100
            if (pdfAnnotation && zone) {
              const annoRect = pdfAnnotation.getBoundingClientRect()
              const zoneRect = zone.getBoundingClientRect()
              fallbackY = Math.max(20, annoRect.top - zoneRect.top)
            }
            syncColumnsToClause(clauseId, {
              scrollList: true,
              forceFallback: true,
              fallbackY,
              commentDelay: 50,
            })
          }),
        150,
      )
    },
    [derivedClauses, syncColumnsToClause],
  )

  const clauseAnnotations = useClauseAnnotations({
    pageWrapperRef: pdfRenderer.pageWrapperRef as React.RefObject<HTMLDivElement>,
    spansWithPages: pdfRenderer.spansWithPages,
    canvasWidth: pdfRenderer.canvasWidth,
    onTagClick: handleInlineTagClick,
  })

  const clauseHighlighting = useClauseHighlighting({
    pageWrapperRef: pdfRenderer.pageWrapperRef as React.RefObject<HTMLDivElement>,
    onSelectionComplete: (selection) => {
      handleCreateTempClause(selection)
    },
    onCoordinateUpdate: (updatedCoordinates, extractedText) => {
      const currentSelectedClause = selectedClauseRef.current
      if (!currentSelectedClause) return

      const updatedClause = {
        ...currentSelectedClause,
        coordinates: updatedCoordinates,
        text: extractedText,
      }

      // Patch the clause's pending container in place (its key/tags/justifications are unchanged).
      // The annotation effect tears down + redraws this clause off the new derivedClauses; no
      // imperative remove/redraw here (it owns the redraw, same path as flag/unflag).
      pendingStore.patchNewClause(currentSelectedClause.uuid, {
        clause: { ...(updatedClause as TempUserClause), coordinates: updatedCoordinates, text: extractedText },
      })
      setSelectedClause(updatedClause)
    },
  })

  // ---- PDF text selection (encapsulates selection + manual highlights) ----
  const pdfTextSel = usePdfTextSelection({
    pageWrapperRef: pdfRenderer.pageWrapperRef,
    isHighlighting: clauseHighlighting.isHighlighting,
    projectUuid: projectUuid || undefined,
    contractUuid: contractId,
    onCreateHighlight,
    // "Comment" brings the highlights rail into view so the new note composer is visible (D1).
    onRequestHighlightsPanel: () => setActiveRailTool('highlights'),
    // Materialize the target page before jumping to a highlight on it (parity with clause scroll).
    ensurePageRendered: pdfRenderer.ensurePageRendered,
  })

  // Saved-highlight comment commit — mirrors handleCommitClauseComment above (ADR 0011); lives
  // here because it appends into pdfTextSel's saved-highlights state. The composer only opens on
  // saved highlights, so `highlightUuid` is always a server uuid. The shared committingCommentRef
  // is safe: clause and highlight uuids never collide.
  const handleCommitHighlightComment = useCallback(
    async (highlightUuid: string) => {
      if (committingCommentRef.current.has(highlightUuid)) return
      const draft = (commentsState.highlightComments[highlightUuid] || '').trim()
      if (!draft || !currentUser) return
      committingCommentRef.current.add(highlightUuid)
      const clientId = crypto.randomUUID()
      const createdAt = new Date().toISOString()
      try {
        await appendHighlightComments(projectUuid, highlightUuid, [
          { comment: draft, createdAt, userId: currentUser.uuid, clientId },
        ])
        pdfTextSel.setSavedHighlights((prev) =>
          prev.map((h) =>
            h.uuid === highlightUuid
              ? {
                  ...h,
                  comments: [
                    ...h.comments,
                    { uuid: clientId, comment: draft, createdAt, userId: currentUser.uuid },
                  ],
                }
              : h,
          ),
        )
        commentsState.setHighlightComments((prev) => {
          const next = { ...prev }
          delete next[highlightUuid]
          return next
        })
      } catch (e) {
        notifications.show({ color: 'red', message: classifyCommitError(e).message })
      } finally {
        committingCommentRef.current.delete(highlightUuid)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commentsState, currentUser, projectUuid, pdfTextSel.setSavedHighlights],
  )

  // Highlight overlay = saved snapshot ⊕ pending store (creates added, pending-deletes hidden).
  const { visibleSaved, pendingCreates } = useMemo(
    () => deriveHighlights(pdfTextSel.savedHighlights, pendingStore.changes),
    [pdfTextSel.savedHighlights, pendingStore.changes],
  )
  usePdfHighlightOverlays(
    pdfRenderer.pageWrapperRef,
    pdfRenderer.pdfRendered,
    pendingCreates,
    visibleSaved,
    // Redraw overlays as pages lazily render / the document re-renders on zoom (changes identity).
    pdfRenderer.spansWithPages,
  )

  // Reset state when contractId changes.
  useEffect(() => {
    setContract(null)
    setPdfData(null)
    setLoading(true)
    setPdfZoom(null)
    setAccessLevel(null)
    setContractOnly(false)
    setRuns([])
    setSelectedRunUuid(null)
    setSelectedClause(null)
    setDetailsClauseUuid(null)
    setAiAnnotationsVisible(true)
    setUserAnnotationsVisible(true)
    commentsState.reset()
    pdfTextSel.reset()
    pdfRenderer.resetState()
    // pendingStore is restored/cleared per-contract by usePendingStorePersistence, not here.
    autoSelectedKeyRef.current = null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId])

  // Reflect the loaded contract's name in the tab title so browser history distinguishes contracts
  // (the route's static `head` title — "Contract Analysis · AERRIS" — is the pre-load fallback).
  useEffect(() => {
    const name = contract?.nameOfAgreement || contract?.name
    if (name) document.title = `${name} · AERRIS`
  }, [contract?.nameOfAgreement, contract?.name])

  // Seed from the route loader (ADR 0027: loader seeds, component owns). The loader already
  // fetched document + PDF + clauses — and handled the 403 → /unauthorized redirect — before this
  // component mounted; mid-flight contract switches are the router's race to serialize now, so
  // the old `cancelled` guard is gone. Everything downstream still mutates this state freely
  // (unified save, version viewing, re-assess), which is why it stays useState rather than
  // reading the loader directly.
  useEffect(() => {
    const { contractData, pdfResponse, clausesData } = loaded
    setContract({ ...contractData, clauses: clausesData })
    setPdfData(pdfResponse)
    setLoading(false)

    // The extraction-run history for the version selector (contracts only). Best-effort:
    // a failure just hides the selector — it never blocks the contract from opening.
    if (!isManagementAccount) {
      getContractRuns(contractData.projectUuid ?? '', contractId)
        .then(setRuns)
        .catch((e) => console.error('Failed to load extraction runs:', e))
    }

    // Reflect the document's project (and thereby client) in the shared context WITHOUT
    // touching the URL: on this page the contract is the identity and its ancestors are
    // derived state, so they must not be persisted as search params. The context resolves
    // the project's client itself (fetching the project if it isn't loaded yet).
    let resolvedClientId: string | undefined
    const resolvedProjectId: string | undefined = contractData.projectUuid || undefined
    if (contractData.projectUuid) {
      resolvedClientId = allProjects.find((p) => p.uuid === contractData.projectUuid)?.clientUuid
      setDerivedScope({ projectId: contractData.projectUuid })
    }

    openDocument({
      uuid: contractId,
      name: contractData.nameOfAgreement || contractData.name || 'Untitled',
      clientId: resolvedClientId,
      projectId: resolvedProjectId,
    })
  }, [loaded, contractId, isManagementAccount, allProjects, setDerivedScope, openDocument])

  // Resolve the caller's access level for this document to gate the edit surface. Best-effort and
  // deny-by-default: any failure leaves it null → read-only. A contract resolves via the contract
  // cascade (matches the @contractAuth.canEdit checks on its write endpoints) and runs independently
  // of the data load. A management account has no contract-scoped access endpoint, so it resolves
  // via its owning project — which is only known once the document has loaded (the URL no longer
  // carries a project id), so the MA lookup starts then; until it lands the surface is read-only.
  useEffect(() => {
    let cancelled = false
    setAccessLevel(null)
    const resolve: Promise<AccessLevel | null> = isManagementAccount
      ? contract?.projectUuid
        ? getProjectAccessLevel(contract.projectUuid)
        : Promise.resolve(null)
      : getContractAccessLevel(contractId)
    resolve
      .then((level) => {
        if (!cancelled) setAccessLevel(level)
      })
      .catch((e) => {
        if (!cancelled) {
          console.error('Failed to resolve contract access level:', e)
          setAccessLevel(null)
        }
      })
    return () => {
      cancelled = true
    }
  }, [contractId, isManagementAccount, contract?.projectUuid])

  // Detect contract-only mode: the project access lookup 403s for a grantee whose only right is
  // the contract cascade. Any other failure (network, 5xx) is NOT contract-only — the viewer then
  // behaves as before rather than redacting links for a project member mid-outage. Management
  // accounts resolve their level through the project already, so the question never arises.
  useEffect(() => {
    if (isManagementAccount || !contract?.projectUuid) return
    let cancelled = false
    getProjectAccessLevel(contract.projectUuid)
      .then((level) => {
        if (!cancelled) {
          setContractOnly(false)
          setProjectAccessLevel(level)
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setContractOnly(e instanceof ForbiddenError)
          setProjectAccessLevel(null)
        }
      })
    return () => {
      cancelled = true
    }
  }, [isManagementAccount, contract?.projectUuid])

  // The granted set that keeps sibling-document links alive in contract-only mode. Clause
  // relationships never cross a project (OukaiServer ADR 0001), and this caller holds no project
  // here — so a related document is reachable for them iff it is itself shared with them.
  useEffect(() => {
    if (!contractOnly) {
      setSharedContractUuids(null)
      return
    }
    let cancelled = false
    getSharedWithMe()
      .then((shared) => {
        if (!cancelled) setSharedContractUuids(new Set(shared.map((s) => s.uuid)))
      })
      .catch(() => {
        if (!cancelled) setSharedContractUuids(new Set())
      })
    return () => {
      cancelled = true
    }
  }, [contractOnly])

  // Whether a link into another contract may render as a link (S8): everything, until contract-only
  // mode says only this document and the caller's other granted documents are reachable.
  const isContractAccessible = useCallback(
    (uuid: string) => {
      if (!contractOnly) return true
      return uuid === contractId || (sharedContractUuids?.has(uuid) ?? false)
    },
    [contractOnly, contractId, sharedContractUuids],
  )

  // Load PDF data when available.
  useEffect(() => {
    if (pdfData?.pdfData) {
      pdfRenderer.loadPdfData(pdfData.pdfData, pdfZoom)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfData, pdfZoom])

  // Add clause annotations once the page layout is ready. Bounding-box and
  // coordinate clauses land on placeholders immediately; the pass re-runs as
  // lazily-rendered pages contribute text spans (clauseAnnotations changes
  // identity with spansWithPages), picking up span-fallback clauses.
  useEffect(() => {
    if (pdfRenderer.pdfRendered && contract?.clauses) {
      const timer = setTimeout(() => {
        clauseAnnotations.addClauseAnnotations(derivedClauses)
        if (aiAnnotationsVisible) clauseAnnotations.showAnnotationsByType('ai')
        else clauseAnnotations.hideAnnotationsByType('ai')
        if (userAnnotationsVisible) clauseAnnotations.showAnnotationsByType('user')
        else clauseAnnotations.hideAnnotationsByType('user')
        // Re-adding wipes per-element state; restore the selection highlight.
        const selected = selectedClauseRef.current
        if (selected) {
          document
            .querySelectorAll(`[data-clause-id="${selected.uuid}"]`)
            .forEach((el) => el.classList.add('selected-annotation'))
        }
      }, ANNOTATION_RENDER_DELAY_MS)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfRenderer.pdfRendered, derivedClauses, clauseAnnotations])

  // Toggle annotation visibility without recreating annotations.
  useEffect(() => {
    if (aiAnnotationsVisible) clauseAnnotations.showAnnotationsByType('ai')
    else clauseAnnotations.hideAnnotationsByType('ai')

    if (userAnnotationsVisible) clauseAnnotations.showAnnotationsByType('user')
    else clauseAnnotations.hideAnnotationsByType('user')
  }, [aiAnnotationsVisible, userAnnotationsVisible, clauseAnnotations])

  // Auto-select + scroll to the clause named in the search param, once its
  // contract is loaded and the PDF has rendered. Guarded by `autoSelectedKeyRef`
  // (set here, cleared on contract change) so it runs once per deep-link —
  // crucially including an analysis→analysis navigation (e.g. the "Updated"
  // badge), which reuses this component rather than remounting it. A one-shot
  // boolean would fire only for the first deep-link of the component's life and
  // silently skip every later one, leaving the target clause un-highlighted.
  useEffect(() => {
    // The bookmark arrives in either id form (short from new links, dashed from old ones);
    // normalize before matching clause UUIDs.
    const targetClauseUuid = normalizeEntityId(clauseFromSearch)
    if (!targetClauseUuid || !contract?.clauses || !pdfRenderer.pdfRendered) return

    const key = `${contractId}:${targetClauseUuid}`
    if (autoSelectedKeyRef.current === key) return

    const clause = contract.clauses.find((c) => c.uuid === targetClauseUuid)
    if (!clause) return
    autoSelectedKeyRef.current = key

    // Delay to let annotations render first.
    setTimeout(() => {
      setSelectedClause(clause)
      setAiAnnotationsVisible(true)
      clauseAnnotations.showAnnotationsByType('ai')

      // Align all three columns at a fixed offset from the top.
      syncColumnsToClause(clause.uuid, {
        forceFallback: true,
        fallbackY: 100,
        scrollList: true,
        listDelay: 500,
        commentDelay: 100,
        pageNumber: clausePageNumber(clause),
      })
    }, 300)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clauseFromSearch, contract?.clauses, pdfRenderer.pdfRendered, contractId])

  // Handle zoom changes.
  useEffect(() => {
    if (pdfZoom !== null) pdfRenderer.rerenderWithZoom(pdfZoom)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfZoom])

  // Memoize clause data. Management-account line items carry no Clause Types, so
  // the tag-based identified filter would hide every row — list all AI clauses.
  const identifiedClauses = useMemo(() => {
    const clauses = isManagementAccount
      ? derivedClauses.filter((c) => c.isUserCreated !== true)
      : clauseAnnotations.getIdentifiedClauses(derivedClauses)
    return sortClausesByPdfPosition(clauses, clauseAnnotations, pdfRenderer.spansWithPages)
  }, [derivedClauses, isManagementAccount, clauseAnnotations, pdfRenderer.spansWithPages])

  const clauseStats = useMemo(
    () => clauseAnnotations.getClauseStats(derivedClauses),
    [derivedClauses, clauseAnnotations],
  )

  // Saved user clauses (from the snapshot) + reviewer-created clauses awaiting save (from the
  // pending store) both surface in derivedClauses with `isUserCreated` set, so this is one filter.
  const userCreatedClauses = useMemo(
    () =>
      sortClausesByPdfPosition(
        clauseAnnotations.getUserCreatedClauses(derivedClauses),
        clauseAnnotations,
        pdfRenderer.spansWithPages,
      ),
    [derivedClauses, clauseAnnotations, pdfRenderer.spansWithPages],
  )

  const userClauseStats = useMemo(() => {
    const allUserClauses = clauseAnnotations.getUserCreatedClauses(derivedClauses)
    const tagCount = allUserClauses.reduce(
      (acc: number, c: Clause) => acc + (c.clauseTypeTags?.length || 0),
      0,
    )
    return { clauseCount: allUserClauses.length, tagCount }
  }, [derivedClauses, clauseAnnotations])

  const reviewClauses = useMemo(
    () =>
      sortClausesByPdfPosition(
        [...identifiedClauses, ...userCreatedClauses],
        clauseAnnotations,
        pdfRenderer.spansWithPages,
      ),
    [identifiedClauses, userCreatedClauses, clauseAnnotations, pdfRenderer.spansWithPages],
  )

  // The clause behind the open floating details panel, re-read from the derived set each render
  // so pending edits/saves refresh it; a deleted clause resolves to null and closes the panel.
  const detailsClause = useMemo(
    () =>
      detailsClauseUuid
        ? reviewClauses.find((c) => c.uuid === detailsClauseUuid) ?? null
        : null,
    [detailsClauseUuid, reviewClauses],
  )

  // Update PDF annotation styling when selected clause changes.
  useEffect(() => {
    const allAnnotations = document.querySelectorAll('[data-clause-id]')
    allAnnotations.forEach((el) => el.classList.remove('selected-annotation'))
    if (selectedClause) {
      document
        .querySelectorAll(`[data-clause-id="${selectedClause.uuid}"]`)
        .forEach((el) => el.classList.add('selected-annotation'))
    }
  }, [selectedClause])

  // ---- Clause interaction handlers ----
  const handleClauseClick = useCallback(
    (clause: Clause) => {
      setSelectedClause(clause)
      const clauseType = clause.isUserCreated ? 'user' : 'ai'
      if (clauseType === 'ai') {
        setAiAnnotationsVisible(true)
        clauseAnnotations.showAnnotationsByType('ai')
      } else {
        setUserAnnotationsVisible(true)
        clauseAnnotations.showAnnotationsByType('user')
      }

      // Align PDF annotation + comment with the clicked list item.
      syncColumnsToClause(clause.uuid, { commentDelay: 50, pageNumber: clausePageNumber(clause) })
    },
    [clauseAnnotations, syncColumnsToClause, clausePageNumber],
  )

  // Open (or toggle closed) the floating extracted-details panel for a clause. Opening also
  // selects the clause and scrolls the PDF to it — the panel anchors beside the annotation,
  // so the clause must be brought into view for the editing context to make sense.
  const handleShowDetails = useCallback(
    (clause: Clause) => {
      if (detailsClauseUuid === clause.uuid) {
        setDetailsClauseUuid(null)
        return
      }
      setDetailsClauseUuid(clause.uuid)
      handleClauseClick(clause)
    },
    [detailsClauseUuid, handleClauseClick],
  )

  const handleTogglePdfAnnotations = useCallback(
    (visible: boolean) => {
      setAiAnnotationsVisible(visible)
      setUserAnnotationsVisible(visible)
      if (visible) {
        clauseAnnotations.showAnnotationsByType('ai')
        clauseAnnotations.showAnnotationsByType('user')
      } else {
        clauseAnnotations.hideAnnotationsByType('ai')
        clauseAnnotations.hideAnnotationsByType('user')
      }
    },
    [clauseAnnotations],
  )

  // Create temporary clause when user finishes a PDF selection.
  const handleCreateTempClause = useCallback(
    (selection: { selectedText?: string; coordinates?: Clause['coordinates'] }) => {
      if (!contract) return

      const tempClause: TempUserClause = {
        // crypto.randomUUID keeps the client id (and MERGE idempotency key) collision-free
        // under StrictMode/rapid create; the `temp-` prefix marks it as not-yet-persisted.
        uuid: `temp-${crypto.randomUUID()}`,
        text: selection.selectedText || '',
        clauseTypeTags: [],
        clauseJustifications: [],
        isUserCreated: true,
        isTemp: true,
        coordinates: selection.coordinates,
      }

      pendingStore.upsert({
        kind: 'newClause',
        clientId: tempClause.uuid,
        clause: tempClause,
        tags: [],
        justifications: [],
      })
      setSelectedClause(tempClause)
      setUserAnnotationsVisible(true)

      // Clear the preview highlight. The annotation effect redraws the new clause off the changed
      // derivedClauses (PRIORITY-2 coordinate path) and re-applies user visibility + the
      // selection highlight on selectedClauseRef — it owns the redraw now (same path as flag/unflag).
      clauseHighlighting.resetSelection()

      // Prompt user to pick tags for the new clause.
      setTagSelectorClauseId(tempClause.uuid)
    },
    [contract, clauseHighlighting, pendingStore],
  )

  const handleConfirmClauseTags = useCallback(
    (tags: string[], comment?: string) => {
      if (!tagSelectorClauseId) return
      // Bundle the chosen tags into the clause's pending container. The derived overlay then
      // re-renders the inline PDF chips through the annotation effect (no imperative tag sync
      // needed — same path as flag/unflag). An optional reviewer comment (I18) is recorded as a
      // justification on each selected tag; createUserClause persists it and it round-trips into
      // the tag tooltip + clause-detail Summary on reload.
      pendingStore.patchNewClause(tagSelectorClauseId, {
        tags: tags.map((clauseType) => ({ clauseType })),
        justifications: comment
          ? tags.map((clauseType) => ({ clauseType: { clauseType }, certainty: 1, justification: comment }))
          : [],
      })
      setTagSelectorClauseId(null)
    },
    [tagSelectorClauseId, pendingStore],
  )

  const handleCancelClauseTags = useCallback(() => setTagSelectorClauseId(null), [])

  // Delete a temp clause (user-created, not yet saved) — drop its whole pending container.
  const handleDeleteClause = useCallback(
    (clauseId: string) => {
      pendingStore.remove(`newClause:${clauseId}`)

      if (selectedClause?.uuid === clauseId) setSelectedClause(null)

      // The annotation effect only re-draws clauses still in the derived set, so a removed
      // clause's PDF elements have to be torn down explicitly here.
      document
        .querySelectorAll(`[data-clause-id="${clauseId}"]`)
        .forEach((element) => element.remove())
    },
    [selectedClause, pendingStore],
  )

  // Undo/redo can drop a pending new clause without going through handleDeleteClause, so
  // reconcile the imperative PDF state off the store: tear down annotation elements for temp
  // clauses no longer pending, and close any selection / tag selector pointing at one.
  // Re-adding on redo needs nothing — the annotation effect draws whatever is in the derived
  // set; only removal has to be explicit (same reason as the teardown in handleDeleteClause).
  useEffect(() => {
    const live = new Set(newClauseChanges.map((c) => c.clientId))
    document.querySelectorAll('[data-clause-id^="temp-"]').forEach((el) => {
      const id = el.getAttribute('data-clause-id')
      if (id && !live.has(id)) el.remove()
    })
    setSelectedClause((prev) => (prev?.uuid.startsWith('temp-') && !live.has(prev.uuid) ? null : prev))
    setTagSelectorClauseId((prev) => (prev?.startsWith('temp-') && !live.has(prev) ? null : prev))
  }, [newClauseChanges])

  const handleEditClause = useCallback(
    (clause: Clause) => {
      const coordinates = newClauseChanges.find((c) => c.clientId === clause.uuid)?.clause.coordinates
      if (!coordinates || coordinates.length === 0) return

      setSelectedClause(clause)
      clauseHighlighting.startEditMode()
      setTimeout(() => {
        clauseHighlighting.drawInteractiveHighlight(coordinates)
      }, ANNOTATION_RENDER_DELAY_MS)
    },
    [newClauseChanges, clauseHighlighting],
  )

  // Mark document reviewed — persists via REST and optimistically updates local state.
  const handleMarkReviewed = useCallback(
    async (reviewed: boolean) => {
      if (!contract || !currentUser) return
      const userId = currentUser.uuid
      const userName = currentUser.name || currentUser.email || 'Unknown User'

      // Optimistic update
      setContract((prev) => {
        if (!prev) return prev
        const existing = prev.reviewedBy || []
        if (reviewed) {
          if (existing.some((r) => r.userId === userId)) return prev
          return {
            ...prev,
            reviewedBy: [...existing, { userId, userName, reviewedAt: new Date().toISOString() }],
          }
        }
        return { ...prev, reviewedBy: existing.filter((r) => r.userId !== userId) }
      })

      try {
        // Management accounts are read-only (reviews dropped for now); only contracts persist reviews.
        if (!isManagementAccount) {
          if (reviewed) await markContractReviewed(projectUuid, contract.uuid, userId, userName)
          else await unmarkContractReviewed(projectUuid, contract.uuid, userId)
        }
      } catch (err) {
        console.error('Failed to persist review status:', err)
        // Roll back optimistic update on failure
        setContract((prev) => {
          if (!prev) return prev
          const existing = prev.reviewedBy || []
          if (reviewed) {
            return { ...prev, reviewedBy: existing.filter((r) => r.userId !== userId) }
          }
          return {
            ...prev,
            reviewedBy: [...existing, { userId, userName, reviewedAt: new Date().toISOString() }],
          }
        })
      }
    },
    [contract, currentUser, isManagementAccount, projectUuid],
  )

  // ---- Save handlers ----
  const { handleSaveAll, saving } = useContractSaves({
    documentKind: isManagementAccount ? 'managementAccount' : 'contract',
    contract,
    setContract,
    currentUserId: currentUser?.uuid,
    projectUuid,
    pendingStore,
    fetchSavedHighlights: pdfTextSel.fetchSavedHighlights,
    setSavedHighlights: pdfTextSel.setSavedHighlights,
  })

  // ---- Page tracking ----
  useEffect(() => {
    const container = pdfRenderer.pageWrapperRef.current
    if (!container || !pdfRenderer.pdfRendered) return

    const updateCurrentPage = () => {
      const pages = container.querySelectorAll<HTMLElement>(
        '.pdf-page-container[data-page-num]',
      )
      if (pages.length === 0) return
      const containerRect = container.getBoundingClientRect()
      const viewportMid = containerRect.top + containerRect.height / 2
      let bestPage = 1
      let bestDistance = Number.POSITIVE_INFINITY
      pages.forEach((el) => {
        const rect = el.getBoundingClientRect()
        const mid = rect.top + rect.height / 2
        const distance = Math.abs(mid - viewportMid)
        if (distance < bestDistance) {
          bestDistance = distance
          bestPage = Number(el.getAttribute('data-page-num')) || 1
        }
      })
      setCurrentPage((prev) => (prev === bestPage ? prev : bestPage))
    }

    updateCurrentPage()
    container.addEventListener('scroll', updateCurrentPage, { passive: true })
    return () => container.removeEventListener('scroll', updateCurrentPage)
  }, [pdfRenderer.pdfRendered, pdfRenderer.pageWrapperRef])

  // ---- Rail zoom/page handlers ----
  const handleRailPageChange = useCallback(
    (page: number) => {
      const container = pdfRenderer.pageWrapperRef.current
      if (!container) return
      const target = container.querySelector<HTMLElement>(
        `.pdf-page-container[data-page-num="${page}"]`,
      )
      if (!target) return
      const containerRect = container.getBoundingClientRect()
      const targetRect = target.getBoundingClientRect()
      const offsetTop = container.scrollTop + (targetRect.top - containerRect.top) - 8
      container.scrollTo({ top: offsetTop, behavior: 'smooth' })
      setCurrentPage(page)
    },
    [pdfRenderer.pageWrapperRef],
  )

  const handleZoomIn = useCallback(() => {
    const current = pdfZoom ?? pdfRenderer.fitWidthScale
    setPdfZoom(Math.min(3, +(current + 0.1).toFixed(2)))
  }, [pdfZoom, pdfRenderer.fitWidthScale])

  const handleZoomOut = useCallback(() => {
    const current = pdfZoom ?? pdfRenderer.fitWidthScale
    setPdfZoom(Math.max(0.4, +(current - 0.1).toFixed(2)))
  }, [pdfZoom, pdfRenderer.fitWidthScale])

  // Derived map: which clauses have any comment (composer draft or persisted).
  const hasCommentByClauseId = useMemo(() => {
    const out: Record<string, boolean> = {}
    const allClauses = contract?.clauses || []
    for (const c of allClauses) {
      const hasDraft = Boolean((commentsState.clauseComments[c.uuid] || '').trim())
      const hasPersisted = (c.comments?.length ?? 0) > 0
      out[c.uuid] = hasDraft || hasPersisted
    }
    return out
  }, [contract, commentsState.clauseComments])

  const showLoadingOverlay = loading || !pdfRenderer.pdfRendered || !contract

  // Stable identity matters: DocumentTextSearch keys its cleanup (which clears
  // search highlights) on this callback, and the route re-renders per lazily
  // rendered page — an inline arrow here would wipe highlights on every render.
  const getPdfContainer = useCallback(
    () => pdfRenderer.pageWrapperRef.current,
    [pdfRenderer.pageWrapperRef],
  )

  const contractTopBar = contract && currentUser && (
    <ContractTopBar
      contract={contract}
      currentUserId={currentUser.uuid}
      onMarkReviewed={
        readOnly
          ? async () => notifyReadOnly()
          : saving || pendingCount > 0
            ? async () =>
                notifications.show({
                  color: 'gray',
                  message: 'Save or discard your changes before marking this contract as reviewed.',
                })
            : handleMarkReviewed
      }
      canEdit={!readOnly}
      getPdfContainer={getPdfContainer}
      // A management account is never risk-assessed, so its badge has nothing to open.
      onOpenRiskDetail={isManagementAccount ? undefined : () => setRiskDetailScope('')}
      onDetailsSaved={
        // Management accounts have no agreement name/date to edit — no gear for them.
        isManagementAccount
          ? undefined
          : (details) => {
              setContract((prev) => (prev ? { ...prev, ...details } : prev))
              // Keep the open-documents tab label in step (openDocument upserts by uuid and
              // leaves clientId/projectId untouched when the keys are absent).
              openDocument({ uuid: contractId, name: details.nameOfAgreement })
            }
      }
    />
  )

  // Flagging an AI clause-type tag as incorrect (and restoring it) is DEFERRED until Save:
  // it records a desired-state delta in the pending store rather than calling the API. The
  // frozen server snapshot (contract.clauses) is the ground truth for the tag's current
  // state; deriveClauses overlays the delta, so the PDF chips + rail update immediately with
  // no network call (flag-then-unflag of the same tag coalesces to nothing — see setTagState).
  const serverTagState = useCallback(
    (clauseUuid: string, clauseType: string): 'correct' | 'flagged' => {
      const clause = contract?.clauses?.find((c) => c.uuid === clauseUuid)
      return (clause?.incorrectClauseTypeTags ?? []).some((t) => t.clauseType === clauseType)
        ? 'flagged'
        : 'correct'
    },
    [contract?.clauses],
  )

  const handleFlagTagIncorrect = useCallback(
    (clauseUuid: string, clauseType: string) => {
      pendingStore.setTagState(clauseUuid, clauseType, 'flagged', serverTagState(clauseUuid, clauseType))
    },
    // pendingStore.setTagState is stable (useCallback in the store hook)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serverTagState],
  )

  const handleUnflagTag = useCallback(
    (clauseUuid: string, clauseType: string) => {
      pendingStore.setTagState(clauseUuid, clauseType, 'correct', serverTagState(clauseUuid, clauseType))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serverTagState],
  )

  // The whole surface is read-only when viewing a prior run (its clauses are superseded nodes) or
  // when the user lacks edit access (READER). Every persisting/edit action becomes a no-op that
  // explains itself. Gating the handlers — not just hiding buttons — protects data integrity
  // regardless of which controls remain visible, and matches the server, which rejects all of these
  // for a non-EDITOR. The authoring controls (add/edit/remove clause, flag) are additionally hidden
  // via the `canEdit` flag passed to buildRailTools.
  const gatedAddClause = readOnly
    ? notifyReadOnly
    : () => clauseHighlighting.startHighlighting('two-click')
  const gatedDelete = readOnly ? () => notifyReadOnly() : handleDeleteClause
  const gatedEdit = readOnly ? () => notifyReadOnly() : handleEditClause
  const gatedFlag = readOnly ? () => notifyReadOnly() : handleFlagTagIncorrect
  const gatedUnflag = readOnly ? () => notifyReadOnly() : handleUnflagTag

  // ── Unified Save surface: SaveBar + confirm modal ──
  const [saveModalOpen, setSaveModalOpen] = useState(false)

  // Grouped summary for the confirm modal (store deltas by target — comments post immediately
  // and never appear here).
  const changeGroups = useMemo<ChangeGroup[]>(() => {
    const c = { flagged: 0, restored: 0, newClauses: 0, hlCreate: 0, hlDelete: 0, detailValues: 0 }
    for (const ch of Object.values(pendingStore.changes)) {
      if (ch.kind === 'tagState') {
        if (ch.target === 'flagged') c.flagged += 1
        else c.restored += 1
      } else if (ch.kind === 'newClause') c.newClauses += 1
      else if (ch.kind === 'highlightCreate') c.hlCreate += 1
      else if (ch.kind === 'highlightDelete') c.hlDelete += 1
      else if (ch.kind === 'detailValue') c.detailValues += 1
    }
    const groups: ChangeGroup[] = []
    if (c.flagged) groups.push({ label: 'Flagged tags', count: c.flagged })
    if (c.restored) groups.push({ label: 'Restored tags', count: c.restored })
    if (c.newClauses) groups.push({ label: 'New clauses', count: c.newClauses })
    if (c.hlCreate) groups.push({ label: 'New highlights', count: c.hlCreate })
    if (c.hlDelete) groups.push({ label: 'Removed highlights', count: c.hlDelete })
    if (c.detailValues) groups.push({ label: 'Detail values', count: c.detailValues })
    return groups
  }, [pendingStore.changes])

  // Discard all pending edits. Tear down PDF elements for pending temp clauses first — the annotation
  // effect only redraws clauses still in the derived set, so a removed temp clause would orphan.
  // Comment composer text is untouched: comments are not pending document changes.
  const handleDiscardAll = useCallback(() => {
    newClauseChanges.forEach((c) =>
      document.querySelectorAll(`[data-clause-id="${c.clientId}"]`).forEach((el) => el.remove()),
    )
    pendingStore.clear()
    setSelectedClause(null)
    setTagSelectorClauseId(null) // a temp clause may have its tag selector open — close it too
    setSaveModalOpen(false)
  }, [newClauseChanges, pendingStore])

  // Every save goes through this wrapper: even a partly-failed save may have recorded a
  // changeset, so an open Change History panel must refetch either way.
  const saveAllTracked = useCallback(async () => {
    const ok = await handleSaveAll()
    setHistoryRefresh((n) => n + 1)
    return ok
  }, [handleSaveAll])

  // Refetch the server truth after an undo landed: the inverse changeset may have touched
  // tags, details, clauses and highlights all at once (mirrors the post-save refetch).
  const refreshAfterUndo = useCallback(async () => {
    await refreshContractSnapshot(
      { router, contractId, projectUuid, setContract },
      {
        fetchSavedHighlights: pdfTextSel.fetchSavedHighlights,
        setSavedHighlights: pdfTextSel.setSavedHighlights,
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, contractId, projectUuid, pdfTextSel.fetchSavedHighlights, pdfTextSel.setSavedHighlights])

  // ── Risk detail modal (ADR 0023 breakdown) ───────────────────────────────────────
  // Re-run the backend evaluation for this contract's fold family, then take the tiers it wrote:
  // the contract read carries the fired rules and provenance, the clause read the per-clause tiers,
  // so both have to come back or the modal would keep showing the evaluation it just replaced.
  const handleReassessRisk = useCallback(async () => {
    if (!projectUuid || reassessing) return
    setReassessing(true)
    try {
      await evaluateContractRisk(projectUuid, contractId)
      await refreshContractSnapshot({ router, contractId, projectUuid, setContract })
    } catch (e) {
      console.error('Failed to re-assess risk:', e)
      notifications.show({
        color: 'red',
        message: e instanceof ForbiddenError
          ? 'Re-assessing risk needs edit access to this project.'
          : 'Could not re-assess this contract\u2019s risk. Please try again.',
      })
    } finally {
      setReassessing(false)
    }
  }, [router, projectUuid, contractId, reassessing])

  // A Task raised from one fired rule. Closing the breakdown as the form opens is deliberate: two
  // stacked modals would leave the reader unsure which Escape closes what.
  const handleCreateTaskFromFinding = useCallback(
    (finding: RiskFinding) => {
      if (!contract) return
      setTaskPrefill(riskFindingPrefill(finding, contract))
      setRiskDetailScope(null)
    },
    [contract],
  )

  // Jump to the clause a floor firing was raised on. Goes through the clause-nav chokepoint rather
  // than selecting locally, so the same deep-link effect that serves cross-contract navigation
  // scrolls all three columns to it.
  const handleGoToClauseFromFinding = useCallback(
    (clauseUuid: string) => {
      setRiskDetailScope(null)
      goToContract(navigate, contractId, clauseUuid)
    },
    [navigate, contractId],
  )

  const handleConfirmSave = useCallback(async () => {
    // Keep the modal open if the save reported failures, so the unsaved rows aren't hidden.
    if (await saveAllTracked()) setSaveModalOpen(false)
  }, [saveAllTracked])

  // Block navigation / tab-close while there is unsaved work (read-only views have none).
  const { blocked: navBlocked, proceed: proceedNav, reset: cancelNav } = useUnsavedChangesGuard(
    !readOnly && pendingCount > 0,
  )

  const tools = buildRailTools({
    contract,
    derivedClauses,
    contractId,
    loading,
    canEdit: !readOnly,
    isContractAccessible,
    identifiedClauses,
    userCreatedClauses,
    reviewClauses,
    selectedClause,
    clauseStats,
    userClauseStats,
    handleClauseClick,
    handleDeleteClause: gatedDelete,
    handleEditClause: gatedEdit,
    onAddClause: gatedAddClause,
    aiAnnotationsVisible,
    userAnnotationsVisible,
    handleTogglePdfAnnotations,
    contractComment: commentsState.contractComment,
    handleContractCommentChange: commentsState.handleContractCommentChange,
    handleCommitContractComment,
    clauseComments: commentsState.clauseComments,
    handleClauseCommentChange: commentsState.handleClauseCommentChange,
    handleCommitClauseComment,
    pendingPdfHighlights: pendingCreates,
    savedHighlights: visibleSaved,
    onJumpToHighlight: pdfTextSel.scrollToHighlight,
    onSetPendingHighlightComment: handleSetPendingHighlightComment,
    onRemovePendingHighlight: handleRemovePendingHighlight,
    onDeleteSavedHighlight: readOnly ? () => notifyReadOnly() : handleDeleteSavedHighlight,
    highlightComments: commentsState.highlightComments,
    handleHighlightCommentChange: commentsState.handleHighlightCommentChange,
    handleCommitHighlightComment,
    noteFocusId: pdfTextSel.noteFocusId,
    onNoteFocusConsumed: pdfTextSel.consumeNoteFocus,
    hasCommentByClauseId,
    handleFlagTagIncorrect: gatedFlag,
    handleUnflagTag: gatedUnflag,
    getDetailSchemas: taxonomy.getDetailSchemas,
    onShowDetails: handleShowDetails,
    detailsOpenClauseUuid: detailsClauseUuid,
    projectUuid,
    historyEnabled: !isManagementAccount,
    historyRefreshToken: historyRefresh,
    onUndoneChangeset: refreshAfterUndo,
    // Creating a Task needs EDITOR+ on the project — but NOT `readOnly`, which also covers viewing a
    // previous extraction run: raising a task about what an old run said is a legitimate thing to do,
    // and the task is a note about the contract, not an edit to it. A reader sees the Tasks tab and its
    // list, and no create affordances.
    onCreateTask: hasEditAccess ? setTaskPrefill : undefined,
    onOpenRiskDetail: isManagementAccount ? undefined : (clause: Clause) => setRiskDetailScope(clause.uuid),
    // A management account belongs to no project, and Tasks are project-scoped (L14).
    tasksEnabled: !isManagementAccount && Boolean(projectUuid),
    taskRefreshToken: taskRefresh,
  })

  // Version selector + read-only banner. Shown only when a contract has more than one extraction run.
  const formatRunOption = (r: ExtractionRunSummary) => {
    const num = r.runNumber != null ? `Run ${r.runNumber}` : 'Run'
    const when = r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ''
    return `${num}${r.current ? ' (current)' : ''}${when ? ` · ${when}` : ''}`
  }
  const versionBar = !isManagementAccount && runs.length > 1 && (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.4rem 1rem',
        borderBottom: '1px solid var(--border-subtle, #e3e0d8)',
        background: viewingHistory ? '#fbf3df' : 'transparent',
        fontSize: '0.8125rem',
      }}
    >
      <label htmlFor="run-version-select" style={{ color: 'var(--text-muted, #6b6658)', fontWeight: 500 }}>
        Version
      </label>
      <select
        id="run-version-select"
        value={selectedRunUuid ?? currentRun?.runUuid ?? ''}
        onChange={(e) => handleSelectRun(e.target.value)}
        style={{ padding: '0.2rem 0.4rem', fontSize: '0.8125rem' }}
      >
        {runs.map((r) => (
          <option key={r.runUuid} value={r.runUuid}>
            {formatRunOption(r)}
          </option>
        ))}
      </select>
      {viewingHistory && (
        <span style={{ color: '#8a6d1a', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          Viewing a previous version — read-only.
          <button
            type="button"
            onClick={() => handleSelectRun(null)}
            style={{
              border: '1px solid #c9a227',
              background: 'transparent',
              color: '#8a6d1a',
              borderRadius: 4,
              padding: '0.15rem 0.5rem',
              cursor: 'pointer',
              fontSize: '0.75rem',
            }}
          >
            Back to current
          </button>
        </span>
      )}
    </div>
  )

  // Read-only notice for users without edit access (READER). The version bar carries its own
  // read-only notice while viewing history, so suppress this one then to avoid a double banner.
  const accessReadOnlyBar = contract && accessLevel === 'READER' && !viewingHistory && (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.4rem 1rem',
        borderBottom: '1px solid var(--border-subtle, #e3e0d8)',
        background: '#f3f4f6',
        color: 'var(--text-muted, #6b6658)',
        fontSize: '0.8125rem',
      }}
    >
      <Lock size={14} />
      You have read-only access to this contract — editing is disabled.
    </div>
  )

  return (
    // Feeds the comment editors' @-mention autocomplete with the users who can access this
    // contract. Management accounts have no contract access list → mentions disabled there.
    <MentionUsersProvider contractUuid={isManagementAccount ? null : contractId}>
    {/* And `#` references to this project's contract material, in the same editors. Project-scoped
        rather than contract-scoped on purpose: a comment on one agreement legitimately points at a
        clause in another, and the server narrows the search to what this reader may actually see. */}
    <MaterialSearchProvider projectUuid={isManagementAccount ? null : projectUuid || null}>
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {contractTopBar}
      {versionBar}
      {accessReadOnlyBar}
      {showLoadingOverlay && (
        <div className="loading-overlay">
          <Loader2 className="loading-spinner" size={32} />
          <span>Loading contract analysis...</span>
        </div>
      )}

      <div className="analysis-two-zone">
        <PdfViewer
          pdfRenderer={pdfRenderer}
          isHighlighting={clauseHighlighting.isHighlighting}
          onPageClick={clauseHighlighting.handlePageClick}
          onPageMouseMove={clauseHighlighting.handlePageMouseMove}
          onPageMouseUp={pdfTextSel.handlePdfTextSelection}
          onPageMouseDown={clauseHighlighting.handlePageMouseDown}
          pdfTextSelection={pdfTextSel.pdfTextSelection}
          onCopySelection={pdfTextSel.handleCopyPdfSelection}
          onHighlightSelection={readOnly ? undefined : pdfTextSel.handleHighlightPdfSelection}
          onCommentSelection={readOnly ? undefined : pdfTextSel.handleCommentPdfSelection}
          floatingPanel={
            detailsClause && (
              <FloatingClauseDetails
                key={detailsClause.uuid}
                clause={detailsClause}
                pageNumber={clausePageNumber(detailsClause)}
                pageWrapperRef={pdfRenderer.pageWrapperRef}
                getDetailSchemas={taxonomy.getDetailSchemas}
                pendingDetailValues={pendingDetailValues}
                onDetailValueChange={readOnly ? undefined : handleDetailValueChange}
                canEdit={!readOnly}
                onClose={() => setDetailsClauseUuid(null)}
              />
            )
          }
        />

        <AnalysisRightRail
          activeTool={activeRailTool}
          onActiveToolChange={setActiveRailTool}
          currentPage={currentPage}
          totalPages={pdfRenderer.totalPages}
          onPageChange={handleRailPageChange}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          tools={tools}
        />
      </div>

      <ClauseTagSelectorModal
        open={tagSelectorClauseId !== null}
        clauseText={
          newClauseChanges.find((c) => c.clientId === tagSelectorClauseId)?.clause.text || undefined
        }
        initialSelected={
          newClauseChanges
            .find((c) => c.clientId === tagSelectorClauseId)
            ?.tags.map((t) => t.clauseType) || []
        }
        initialComment={
          newClauseChanges.find((c) => c.clientId === tagSelectorClauseId)?.justifications[0]?.justification || ''
        }
        onCancel={handleCancelClauseTags}
        onConfirm={handleConfirmClauseTags}
      />

      {/* Unified Save surface — the single way to persist pending edits (hidden in read-only mode). */}
      {!readOnly && (
        <SaveBar
          count={pendingCount}
          saving={saving}
          canUndo={pendingStore.canUndo}
          canRedo={pendingStore.canRedo}
          onUndo={pendingStore.undo}
          onRedo={pendingStore.redo}
          onReview={() => setSaveModalOpen(true)}
          onDiscard={handleDiscardAll}
        />
      )}
      <SaveChangesModal
        open={saveModalOpen}
        groups={changeGroups}
        saving={saving}
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveModalOpen(false)}
      />
      {/* One create-Task form for every affordance on the page (validity issue, clause, Tasks tab). It
          carries its own project-scoped mention provider — the contract-scoped MentionUsersProvider
          above feeds the clause/document comment editors, whose access boundary is the contract. */}
      <CreateTaskModal
        projectUuid={projectUuid}
        opened={taskPrefill !== null}
        onClose={() => setTaskPrefill(null)}
        onCreated={() => setTaskRefresh((n) => n + 1)}
        prefill={taskPrefill ?? undefined}
        // Every anchor raised from this page points into the open contract, so its grantees join
        // the assignee/Reviewer/mention pool (S6) — and a contract-only EDITOR is admitted to the
        // picker endpoint at all.
        anchorContractUuids={isManagementAccount ? undefined : [contractId]}
      />

      {/* The full risk breakdown, opened from the top-bar badge (contract-wide) or a clause's tier
          dot (clause-scoped). Rendered from the route because it needs the contract, the create-Task
          seam and the post-re-assess refetch, none of which the rail or the top bar can reach. */}
      {riskDetailScope !== null && contract && (
        <RiskDetailModal
          contract={contract}
          clause={riskDetailScope ? (contract.clauses ?? []).find((c) => c.uuid === riskDetailScope) : null}
          onClose={() => setRiskDetailScope(null)}
          onViewWholeContract={riskDetailScope ? () => setRiskDetailScope('') : undefined}
          onCreateTask={hasEditAccess ? handleCreateTaskFromFinding : undefined}
          onGoToClause={handleGoToClauseFromFinding}
          // Evaluating writes the whole fold family's tiers, so the server gates it on PROJECT
          // edit (@projectAuth.canEdit) — contract-level Effective Access is not enough. Gate the
          // button the same way, so a contract-only EDITOR never discovers the rule via a 403.
          onReassess={
            (projectAccessLevel === 'ADMIN' || projectAccessLevel === 'EDITOR') && projectUuid
              ? handleReassessRisk
              : undefined
          }
          reassessing={reassessing}
        />
      )}

      <UnsavedChangesPrompt
        open={navBlocked}
        count={pendingCount}
        saving={saving}
        onSave={async () => {
          // Only leave if the save fully succeeded; otherwise stay so the failed rows aren't lost.
          if (await saveAllTracked()) proceedNav()
        }}
        onDiscard={() => {
          handleDiscardAll()
          proceedNav()
        }}
        onCancel={cancelNav}
      />
    </div>
    </MaterialSearchProvider>
    </MentionUsersProvider>
  )
}
